/**
 * CalendarGantt - Componente de Gráfico de Gantt com visual glassmorphism e estilo Fluent UI.
 *
 * Exibe tarefas hierárquicas com suporte a edição inline, drag & drop, redimensionamento de datas, dependências entre tarefas, colapso/expansão de subtarefas, zoom e exportação para Excel.
 * Usa fontes Inter para títulos e Space Grotesk para o restante.
 *
 * @component
 * @param {Object} props
 * @param {TaskEvent[]} props.tasks - Lista de tarefas a serem exibidas no Gantt.
 * @param {Date} props.ganttStart - Data inicial do Gantt (visível).
 * @param {Date} props.ganttEnd - Data final do Gantt (visível).
 * @param {number} [props.colWidth=28] - Largura de cada coluna de dia (em px). Permite zoom.
 * @param {"light"|"dark"} [props.theme="light"] - Tema de cor.
 *
 * @example
 * // Exemplo de uso básico:
 * import { CalendarGantt } from "./CalendarGantt";
 *
 * const tasks = [
 *   {
 *     id: "1",
 *     title: "Planejamento",
 *     dtstart: new Date("2025-06-01"),
 *     dtend: new Date("2025-06-05"),
 *     assignedTo: ["Alice"],
 *     status: "Em andamento",
 *     priority: "Alta",
 *     progress: 40,
 *     color: "#2563eb",
 *     children: [
 *       {
 *         id: "1.1",
 *         parentId: "1",
 *         title: "Briefing",
 *         dtstart: new Date("2025-06-01"),
 *         dtend: new Date("2025-06-02"),
 *         assignedTo: ["Bob"],
 *         status: "Concluído",
 *         priority: "Média",
 *         progress: 100,
 *         color: "#22c55e"
 *       }
 *     ]
 *   }
 * ];
 *
 * <CalendarGantt
 *   tasks={tasks}
 *   ganttStart={new Date("2025-06-01")}
 *   ganttEnd={new Date("2025-06-10")}
 *   colWidth={28}
 *   theme="light"
 * />
 *
 * @example
 * // Exemplo com tema escuro e largura de coluna customizada:
 * <CalendarGantt
 *   tasks={tasks}
 *   ganttStart={new Date("2025-06-01")}
 *   ganttEnd={new Date("2025-06-30")}
 *   colWidth={56}
 *   theme="dark"
 * />
 *
 * @returns {JSX.Element} O componente de Gantt Chart renderizado.
 *
 * @features
 * - Visualização hierárquica de tarefas (com colapso/expansão)
 * - Edição inline de tarefas (nome, responsável, status, prioridade, datas, dependências)
 * - Drag & drop para reordenar tarefas no mesmo nível
 * - Redimensionamento de datas por arraste (início/fim)
 * - Criação e remoção de dependências entre tarefas
 * - Zoom (ajuste da largura do dia)
 * - Navegação por semana
 * - Exportação para Excel
 * - Destaque do dia atual
 * - Tooltip com detalhes da tarefa
 */

import React, { useRef, useLayoutEffect, useState, useCallback } from "react";
import {
  format,
  addDays,
  startOfDay,
  endOfDay,
  differenceInDays,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  isSameWeek,
  getMonth,
  getYear,
  getWeek,
  addWeeks,
} from "date-fns";
import { GanttBar } from "./CalendarGanttBar";
import clsx from "clsx";
import Button from "../Button/Button";
import Tooltip from "../Tooltip/Tooltip";
import { Form } from "../Form/Form";
import type { FormConfig } from "../Form/Form";
import { generateXLSX } from "../List/GenerateXLSX";
import type { TaskEvent } from "./Calendar";

// Move interface/type declarations here


type EditState = {
  id: string;
  title: string;
  responsavel: string;
  status: string;
  prioridade: string;
  inicio: string;
  fim: string;
  dependencies?: string[];
};

type CalendarGanttProps = {
  tasks: TaskEvent[];
  ganttStart: Date;
  ganttEnd: Date;
  colWidth?: number;
  theme?: "light" | "dark";
};

// Defina os níveis de zoom permitidos (em pixels por dia)
const ZOOM_LEVELS = [14, 20, 28, 40, 56, 80, 112];

function CalendarGantt({
  tasks,
  ganttStart,
  ganttEnd,
  colWidth: initialColWidth = 28,
  theme = "light",
}: CalendarGanttProps) {
  // No topo do arquivo:
  const [toast, setToast] = useState<{ msg: string; type?: string } | null>(
    null
  );
  function showToast(msg: string, type?: string) {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  }

  // Memoize buildTaskTree inside the component
  const buildTaskTree = useCallback((events: TaskEvent[]) => {
    const map = new Map<string, TaskEvent>();
    const roots: TaskEvent[] = [];
    events.forEach((ev) => map.set(ev.id, { ...ev, children: [] }));
    map.forEach((ev) => {
      if (ev.parentId && map.has(ev.parentId)) {
        map.get(ev.parentId)!.children!.push(ev);
      } else {
        roots.push(ev);
      }
    });
    return roots;
  }, []);
  const [colWidth, setColWidth] = useState(initialColWidth);
  const [editing, setEditing] = useState<EditState | null>(null);
  const [localTasks, setLocalTasks] = useState<TaskEvent[]>(tasks);
  const [collapsedIds, setCollapsedIds] = useState<string[]>([]); // NOVO
  const [dependencyFromId, setDependencyFromId] = useState<string | null>(null); // NOVO
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [dragOverTaskId, setDragOverTaskId] = useState<string | null>(null); // NOVO

  // Atualiza localTasks se tasks mudar
  React.useEffect(() => {
    setLocalTasks(tasks);
  }, [tasks]);

  // Estados para navegação por semana
  const [viewStart, setViewStart] = useState(ganttStart);
  const [viewEnd, setViewEnd] = useState(ganttEnd);

  // Atualiza viewStart/viewEnd se ganttStart/ganttEnd mudar
  React.useEffect(() => {
    setViewStart(ganttStart);
    setViewEnd(ganttEnd);
  }, [ganttStart, ganttEnd]);

  // Memoize handlers de navegação por semana
  const handlePrevWeek = useCallback(() => {
    setViewStart((prev) => addWeeks(prev, -1));
    setViewEnd((prev) => addWeeks(prev, -1));
  }, []);
  const handleNextWeek = useCallback(() => {
    setViewStart((prev) => addWeeks(prev, 1));
    setViewEnd((prev) => addWeeks(prev, 1));
  }, []);

  const getMonthRanges = useCallback((start: Date, end: Date) => {
    const months: { start: Date; end: Date; label: string; days: number }[] =
      [];
    let current = startOfMonth(start);
    while (current <= end) {
      const monthEnd = endOfMonth(current) < end ? endOfMonth(current) : end;
      const days = differenceInDays(monthEnd, current) + 1;
      months.push({
        start: current,
        end: monthEnd,
        label: format(current, "MMMM yyyy"),
        days,
      });
      current = addDays(monthEnd, 1);
    }
    return months;
  }, []);

  // Gera os intervalos de semanas
  const getWeekRanges = useCallback((start: Date, end: Date) => {
    const weeks: { start: Date; end: Date; label: string; days: number }[] = [];
    let current = startOfWeek(start, { weekStartsOn: 1 });
    while (current <= end) {
      const weekEnd =
        endOfWeek(current, { weekStartsOn: 1 }) < end
          ? endOfWeek(current, { weekStartsOn: 1 })
          : end;
      const days = differenceInDays(weekEnd, current) + 1;
      weeks.push({
        start: current,
        end: weekEnd,
        label: format(current, "w 'semana'"),
        days,
      });
      current = addDays(weekEnd, 1);
    }
    return weeks;
  }, []);
  // Gera os intervalos de semanas
  // (Removido: função duplicada getWeekRanges)

  // Use viewStart/viewEnd no lugar de ganttStart/ganttEnd nas variáveis dependentes
  const totalDays =
    (viewEnd.getTime() - viewStart.getTime()) / (1000 * 60 * 60 * 24) + 1;
  const monthRanges = getMonthRanges(viewStart, viewEnd);
  const weekRanges = getWeekRanges(viewStart, viewEnd);

  const barRefs = React.useRef<Record<string, React.RefObject<HTMLDivElement>>>(
    {}
  ).current;
  Object.keys(barRefs).forEach((k) => delete barRefs[k]);
  const tasksTree = React.useMemo(
    () => buildTaskTree(localTasks),
    [localTasks, buildTaskTree]
  );
  // Memoize handleSaveEdit
  const handleSaveEdit = useCallback(
    (edit: EditState) => {
      setLocalTasks((prev) =>
        prev.map((ev) =>
          ev.id === edit.id
            ? {
                ...ev,
                title: edit.title,
                assignedTo: edit.responsavel
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
                status: edit.status,
                priority: edit.prioridade,
                dtstart: edit.inicio ? new Date(edit.inicio) : ev.dtstart,
                dtend: edit.fim ? new Date(edit.fim) : ev.dtend,
                dependencies: edit.dependencies ? edit.dependencies : [],
              }
            : ev
        )
      );
      setEditing(null);
    },
    [setLocalTasks]
  );

  // Memoize toggleCollapse
  const toggleCollapse = useCallback(
    (id: string) => {
      setCollapsedIds((prev) =>
        prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
      );
    },
    [setCollapsedIds]
  );

  // Memoize handleRemoveDependency
  const handleRemoveDependency = useCallback(
    (fromId: string, toId: string) => {
      setLocalTasks((prev) =>
        prev.map((ev) =>
          ev.id === toId
            ? {
                ...ev,
                dependencies: (ev.dependencies ?? []).filter(
                  (dep) => dep !== fromId
                ),
              }
            : ev
        )
      );
    },
    [setLocalTasks]
  );

  // Memoize renderGanttRows
  const renderGanttRows = useCallback(
    (
      tasks: TaskEvent[],
      level: number,
      ganttStart: Date,
      totalDays: number,
      colWidth: number,
      barRefs: Record<string, React.RefObject<HTMLDivElement>>,
      editing: EditState | null,
      setEditing: React.Dispatch<React.SetStateAction<EditState | null>>,
      onSaveEdit: (edit: EditState) => void,
      setLocalTasks: React.Dispatch<React.SetStateAction<TaskEvent[]>>,
      collapsedIds: string[],
      toggleCollapse: (id: string) => void
    ) => {
      return tasks.map((event) => {
        const start = startOfDay((event.dtstart || event.date) as Date);
        const end = endOfDay((event.dtend || event.date) as Date);
        const left =
          ((start.getTime() - ganttStart.getTime()) /
            (1000 * 60 * 60 * 24) /
            totalDays) *
          100;
        const width =
          ((end.getTime() - start.getTime()) /
            (1000 * 60 * 60 * 24) /
            totalDays) *
          100;
        const barRef = barRefs[event.id] ?? React.createRef<HTMLDivElement>();
        barRefs[event.id] = barRef;

        // Custom columns
        const responsavel = event.assignedTo?.join(", ") || "-";
        const status = event.status || "-";
        const prioridade = event.priority || "-";
        const inicioDate =
          event.dtstart instanceof Date ? event.dtstart : event.date;
        const fimDate = event.dtend instanceof Date ? event.dtend : event.date;
        const inicio = inicioDate ? format(inicioDate, "yyyy-MM-dd") : "";
        const fim = fimDate ? format(fimDate, "yyyy-MM-dd") : "";
        const duracao =
          inicioDate && fimDate
            ? differenceInDays(fimDate, inicioDate) + 1
            : "-";
        const progresso = event.progress ?? 0;

        const isEditing = editing?.id === event.id;

        // Tooltip content
        const tooltipContent = (
          <div>
            <div>
              <b>{event.title}</b>
            </div>
            <div>Responsável: {responsavel}</div>
            <div>Status: {String(status)}</div>
            <div>Prioridade: {String(prioridade)}</div>
            <div>Início: {inicio}</div>
            <div>Fim: {fim}</div>
            <div>Duração: {duracao}d</div>
            <div>Progresso: {progresso}%</div>
          </div>
        );

        // Inline edit form usando Form
        function handleEditSaveForm(formData: Record<string, unknown>) {
          onSaveEdit({
            id: event.id,
            title: String(formData.title ?? ""),
            responsavel: String(formData.responsavel ?? ""),
            status: String(formData.status ?? ""),
            prioridade: String(formData.prioridade ?? ""),
            inicio: String(formData.inicio ?? ""),
            fim: String(formData.fim ?? ""),
            dependencies:
              typeof formData.dependencies === "string"
                ? formData.dependencies
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                : [],
          });
        }

        const isCollapsed = collapsedIds.includes(event.id);

        return (
          <React.Fragment key={event.id}>
            <div
              className={clsx(
                "gantt-row flex relative mb-2 items-center rounded-xl shadow-sm",
                "hover:bg-blue-100/40 transition",
                "bg-white/60 backdrop-blur-md border border-white/30",
                "font-grotesk",
                level > 0 && `ml-[${level * 24}px]`,
                draggedTaskId === event.id && "opacity-50"
              )}
              role="row"
              aria-label={`Tarefa: ${event.title}`}
              tabIndex={0} // Permite foco via teclado
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setEditing({
                    id: event.id,
                    title: event.title,
                    responsavel,
                    status: String(status),
                    prioridade: String(prioridade),
                    inicio,
                    fim,
                    dependencies: event.dependencies ?? [],
                  });
                }
                if (e.key === "Delete") {
                  if (
                    window.confirm(
                      `Deseja realmente deletar a tarefa "${event.title}"?`
                    )
                  ) {
                    setLocalTasks((prev) =>
                      prev.filter((ev) => ev.id !== event.id)
                    );
                    showToast("Tarefa deletada", "warning");
                  }
                }
              }}
              onDragStart={() => setDraggedTaskId(event.id)}
              onDragEnd={() => setDraggedTaskId(null)}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                setDragOverTaskId(event.id);
              }}
              onDragLeave={() => setDragOverTaskId(null)}
              onDrop={() => {
                if (!draggedTaskId || draggedTaskId === event.id) return;
                setLocalTasks((prev) => {
                  // Função recursiva para reordenar no mesmo nível
                  function reorder(tasks: TaskEvent[]): TaskEvent[] {
                    const idxFrom = tasks.findIndex(
                      (t) => t.id === draggedTaskId
                    );
                    const idxTo = tasks.findIndex((t) => t.id === event.id);
                    if (idxFrom === -1 || idxTo === -1) {
                      // Tenta nos filhos
                      return tasks.map((t) =>
                        t.children ? { ...t, children: reorder(t.children) } : t
                      );
                    }
                    // Só permite se for no mesmo nível
                    if (tasks[idxFrom].parentId !== tasks[idxTo].parentId) {
                      return tasks;
                    }
                    const newTasks = [...tasks];
                    const [movedTask] = newTasks.splice(idxFrom, 1);
                    newTasks.splice(idxTo, 0, movedTask);
                    return newTasks;
                  }
                  return reorder(prev);
                });
              }}
              onDoubleClick={() =>
                setEditing({
                  id: event.id,
                  title: event.title,
                  responsavel,
                  status: String(status),
                  prioridade: String(prioridade),
                  inicio,
                  fim,
                  dependencies: event.dependencies ?? [],
                })
              }
              onContextMenu={(e) => {
                e.preventDefault();
                setEditing(null);
                const menu = document.createElement("div");
                menu.className =
                  "absolute z-50 bg-white rounded shadow-lg overflow-hidden";
                menu.style.left = `${e.clientX}px`;
                menu.style.top = `${e.clientY}px`;
                menu.style.transform = "translate(-50%, -50%)";
                menu.style.pointerEvents = "auto";
                menu.innerHTML = `
                  <div class="p-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100" onclick="editTask('${event.id}')">
                    Editar tarefa
                  </div>
                  <div class="p-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100" onclick="deleteTask('${event.id}', '${event.title}')">
                    Deletar tarefa
                  </div>
                `;
                document.body.appendChild(menu);
                const closeMenu = () => {
                  menu.remove();
                  document.removeEventListener("click", closeMenu);
                };
                document.addEventListener("click", closeMenu);
              }}
              style={{ cursor: "grab" }}
            >
              {isEditing ? (
                <div className="w-full">
                  <Form
                    config={{
                      title: "Editar tarefa",
                      fields: [
                        [
                          {
                            id: "title",
                            type: "input",
                            label: "Nome",
                            required: true,
                          },
                          {
                            id: "responsavel",
                            type: "input",
                            label: "Responsável",
                          },
                          {
                            id: "status",
                            type: "input",
                            label: "Status",
                          },
                          {
                            id: "prioridade",
                            type: "input",
                            label: "Prioridade",
                          },
                          {
                            id: "inicio",
                            type: "input",
                            label: "Início",
                            inputType: "date",
                          },
                          {
                            id: "fim",
                            type: "input",
                            label: "Fim",
                            inputType: "date",
                          },
                          {
                            id: "dependencies",
                            type: "input",
                            label: "Dependências (IDs separados por vírgula)",
                          },
                        ],
                      ],
                      buttons: {
                        custom: [
                          {
                            title: "Salvar",
                            action: "submit",
                            icon: "submit",
                            onClick: (formData) => handleEditSaveForm(formData as Record<string, unknown>),
                          },
                          {
                            title: "Cancelar",
                            action: "cancel",
                            icon: "Limpar",
                            onClick: () => setEditing(null),
                          },
                        ],
                      },
                    } as FormConfig}
                    collectionName="gantt-edit"
                    mode="update"
                    onSubmit={handleEditSaveForm}
                    theme="light"
                    buttonProps={{ size: "small" }}
                    inputFieldProps={{}}
                  />
                </div>
              ) : (
                <>
                  <div
                    className="gantt-col w-48 truncate min-w-[60px] text-[0.8rem] sm:text-base"
                  >
                    {event.title}
                  </div>
                  <div className="gantt-col w-32 truncate hidden sm:block">
                    {responsavel}
                  </div>
                  <div className="gantt-col w-24 truncate hidden md:block">
                    {String(status)}
                  </div>
                  <div className="gantt-col w-20 truncate">
                    {String(prioridade)}
                  </div>
                  <div className="gantt-col w-24 truncate">{inicio}</div>
                  <div className="gantt-col w-24 truncate">{fim}</div>
                  <div className="gantt-col w-16 truncate text-center">
                    {duracao}d
                  </div>
                  <div className="gantt-col w-20 truncate text-center">
                    {progresso}%
                  </div>
                  <div className="gantt-col w-32 truncate">
                    {event.dependencies?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {event.dependencies.map((depId) => (
                          <span
                            key={depId}
                            className="inline-flex items-center bg-blue-100 text-blue-700 rounded px-1 text-xs mr-1"
                          >
                            {depId}
                            <button
                              type="button"
                              className="ml-1 text-red-500 hover:text-red-700"
                              title="Remover dependência"
                              onClick={() =>
                                handleRemoveDependency(depId, event.id)
                              }
                              style={{ fontSize: 12, lineHeight: 1 }}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </div>
                  <div className="flex-1 relative min-w-0">
                    <Tooltip tooltip={tooltipContent}>
                      <div
                        className="h-7"
                        onDoubleClick={() =>
                          setEditing({
                            id: event.id,
                            title: event.title,
                            responsavel,
                            status: String(status),
                            prioridade: String(prioridade),
                            inicio,
                            fim,
                            dependencies: event.dependencies ?? [],
                          })
                        }
                      >
                        <GanttBar
                          ref={barRef}
                          left={left}
                          width={width}
                          color={event.color || "#3182ce"}
                          title={`${event.title} (${inicio} - ${fim})`}
                          progress={progresso}
                          progressColor="#38bdf8"
                          showProgressBar
                          onDrag={(delta) => {
                            // Atualiza datas de início/fim ao arrastar
                            if (delta === 0) return;
                            const newStart = addDays(start, delta);
                            const newEnd = addDays(end, delta);
                            setLocalTasks((prev) =>
                              prev.map((ev) =>
                                ev.id === event.id
                                  ? { ...ev, dtstart: newStart, dtend: newEnd }
                                  : ev
                              )
                            );
                          }}
                          onResizeStart={(delta) => {
                            // Redimensiona início
                            if (delta === 0) return;
                            const newStart = addDays(start, delta);
                            if (newStart <= end) {
                              setLocalTasks((prev) =>
                                prev.map((ev) =>
                                  ev.id === event.id
                                    ? { ...ev, dtstart: newStart }
                                    : ev
                                )
                              );
                            }
                          }}
                          onResizeEnd={(delta) => {
                            // Redimensiona fim
                            if (delta === 0) return;
                            const newEnd = addDays(end, delta);
                            if (newEnd >= start) {
                              setLocalTasks((prev) =>
                                prev.map((ev) =>
                                  ev.id === event.id
                                    ? { ...ev, dtend: newEnd }
                                    : ev
                                )
                              );
                            }
                          }}
                          onStartDependency={() => setDependencyFromId(event.id)}
                          onCompleteDependency={
                            dependencyFromId && dependencyFromId !== event.id
                              ? () => {
                                  setLocalTasks((prev) =>
                                    prev.map((ev) =>
                                      ev.id === event.id
                                        ? {
                                            ...ev,
                                            dependencies: [
                                              ...(ev.dependencies ?? []),
                                              dependencyFromId,
                                            ].filter(
                                              (v, i, arr) =>
                                                arr.indexOf(v) === i
                                            ),
                                          }
                                        : ev
                                    )
                                  );
                                  setDependencyFromId(null);
                                }
                              : undefined
                          }
                          className={clsx(
                            dependencyFromId && dependencyFromId !== event.id
                              ? "ring-2 ring-blue-600 ring-offset-2"
                              : "",
                            typeof event.priority === "string" &&
                              event.priority.toLowerCase() === "alta"
                              ? "border-2 border-red-500"
                              : ""
                          )}
                        />
                      </div>
                    </Tooltip>
                  </div>
                </>
              )}
            </div>
            {/* Renderiza filhos apenas se não estiver colapsado */}
            {event.children &&
              event.children.length > 0 &&
              !isCollapsed &&
              renderGanttRows(
                event.children,
                level + 1,
                ganttStart,
                totalDays,
                colWidth,
                barRefs,
                editing,
                setEditing,
                onSaveEdit,
                setLocalTasks,
                collapsedIds,
                toggleCollapse
              )}
            {dragOverTaskId === event.id && draggedTaskId && draggedTaskId !== event.id && (
              <div
                className="absolute left-0 right-0 h-1 bg-blue-400/70 rounded-full z-50"
                style={{ top: -4 }}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        );
      });
    },
    [
      dependencyFromId,
      draggedTaskId,
      dragOverTaskId,
      handleRemoveDependency,
    ]
  );

  const ganttContainerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // (removed unused arrows state and useLayoutEffect for arrows)

  // Zoom handlers
  const handleZoom = useCallback(
    (direction: "in" | "out") => {
      const idx = ZOOM_LEVELS.indexOf(colWidth);
      if (direction === "in" && idx < ZOOM_LEVELS.length - 1) {
        setColWidth(ZOOM_LEVELS[idx + 1]);
      }
      if (direction === "out" && idx > 0) {
        setColWidth(ZOOM_LEVELS[idx - 1]);
      }
    },
    [colWidth]
  );

  // Scroll horizontal para o início ao mudar o zoom
  useLayoutEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  }, [colWidth, ganttStart, ganttEnd]);

  // Gera os intervalos de meses

  // Gera os intervalos de semanas (mantém weekRanges para ganttStart/ganttEnd se necessário)
  // Removido: declaração duplicada de weekRanges

  // Memoize export handler
  const handleExportExcel = useCallback(async () => {
    const header = [
      "ID",
      "Nome",
      "Responsável",
      "Status",
      "Prioridade",
      "Início",
      "Fim",
      "Duração",
      "Progresso",
      "Dependências",
    ];
    function collectRows(tasks: TaskEvent[], rows: string[][] = []) {
      for (const t of tasks) {
        const inicio = t.dtstart instanceof Date ? t.dtstart : t.date;
        const fim = t.dtend instanceof Date ? t.dtend : t.date;
        const duracao =
          inicio && fim
            ? Math.abs(+fim - +inicio) / (1000 * 60 * 60 * 24) + 1
            : "";
        rows.push([
          String(t.id),
          String(t.title),
          t.assignedTo?.join(", ") ?? "",
          String(t.status ?? ""),
          String(t.priority ?? ""),
          inicio ? format(inicio, "yyyy-MM-dd") : "",
          fim ? format(fim, "yyyy-MM-dd") : "",
          String(duracao),
          t.progress !== undefined ? String(t.progress) : "",
          t.dependencies?.join(", ") ?? "",
        ]);
        if (t.children?.length) collectRows(t.children, rows);
      }
      return rows;
    }
    const rows = collectRows(localTasks);

    const { blob, filename } = await generateXLSX(header, rows, {
      filenamePrefix: "gantt",
    });

    // Download
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, [localTasks]);

  return (
    <div
      className={clsx(
        "w-full h-full flex flex-col min-h-0 min-w-0 font-grotesk", // Flex container ocupa todo espaço
        theme === "dark" ? "text-gray-100" : "text-gray-900"
      )}
      role="region"
      aria-label="Gráfico de Gantt de tarefas"
      tabIndex={0}
      style={{ background: theme === "dark" ? "#181f2a" : "#f8fafc" }}
    >
      {/* Header de ações */}
      <div className="flex gap-2 mb-2 items-center px-4 pt-4 font-inter">
        <Button
          size="small"
          onClick={handlePrevWeek}
          title="Semana anterior"
          className={clsx(
            "bg-white/60 hover:bg-white/80 border border-gray-200 shadow rounded-lg text-blue-700 font-inter",
            theme === "dark" && "bg-gray-800/80 hover:bg-gray-900/80 text-cyan-300 border-gray-700"
          )}
        >
          ← Semana
        </Button>
        <Button
          size="small"
          onClick={handleNextWeek}
          title="Próxima semana"
          className={clsx(
            "bg-white/60 hover:bg-white/80 border border-gray-200 shadow rounded-lg text-blue-700 font-inter",
            theme === "dark" && "bg-gray-800/80 hover:bg-gray-900/80 text-cyan-300 border-gray-700"
          )}
        >
          Semana →
        </Button>
        <Button
          size="small"
          onClick={() => handleZoom("out")}
          disabled={colWidth === ZOOM_LEVELS[0]}
          title="Diminuir zoom"
          className={clsx(
            "bg-white/60 hover:bg-white/80 border border-gray-200 shadow rounded-lg text-blue-700 font-inter",
            theme === "dark" && "bg-gray-800/80 hover:bg-gray-900/80 text-cyan-300 border-gray-700"
          )}
        >
          -
        </Button>
        <span className="text-xs font-semibold font-inter">
          {colWidth}px/dia
        </span>
        <Button
          size="small"
          onClick={() => handleZoom("in")}
          disabled={colWidth === ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
          title="Aumentar zoom"
          className={clsx(
            "bg-white/60 hover:bg-white/80 border border-gray-200 shadow rounded-lg text-blue-700 font-inter",
            theme === "dark" && "bg-gray-800/80 hover:bg-gray-900/80 text-cyan-300 border-gray-700"
          )}
        >
          +
        </Button>
        <Button
          size="small"
          className={clsx(
            "bg-green-600 hover:bg-green-700 text-white font-inter border border-green-700 shadow rounded-lg"
          )}
          onClick={handleExportExcel}
        >
          Exportar Excel
        </Button>
      </div>
      {/* Só o gráfico tem rolagem */}
      <div className="flex-1 flex flex-col min-h-0 min-w-0 font-grotesk">
        <div
          className={clsx(
            "gantt-container",
            "flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-auto",
            "relative rounded-xl",
            theme === "dark"
              ? "bg-gray-900/80 border border-gray-700"
              : "bg-white/60 border border-white/30",
            "backdrop-blur-lg shadow-lg mx-4 scroll-smooth touch-pan-x",
            "font-grotesk"
          )}
          ref={ganttContainerRef}
          style={{
            maxHeight: "100%",
            minHeight: 400,
          }}
        >
          <div
            ref={scrollRef}
            className="relative font-grotesk"
            style={{ minWidth: totalDays * colWidth + 400 }}
          >
            {/* Cabeçalho fixo */}
            <div
              className={clsx(
                "gantt-header flex flex-col mb-2 sticky top-0 z-30 font-inter",
                theme === "dark"
                  ? "bg-gray-900/90 backdrop-blur-md"
                  : "bg-white/80 backdrop-blur-md",
                "text-base sm:text-base text-[0.85rem]"
              )}
              role="rowgroup"
              aria-label="Cabeçalho do gráfico de Gantt"
            >
              {/* Linha de meses */}
              <div className="flex font-inter">
                <div className="gantt-col w-48" />
                <div className="gantt-col w-32" />
                <div className="gantt-col w-24" />
                <div className="gantt-col w-20" />
                <div className="gantt-col w-24" />
                <div className="gantt-col w-24" />
                <div className="gantt-col w-16" />
                <div className="gantt-col w-20" />
                <div className="flex-1 relative flex">
                  {monthRanges.map((month, i) => (
                    <div
                      key={i}
                      className={clsx(
                        "text-xs font-bold text-center border-l",
                        theme === "dark"
                          ? "border-gray-700 bg-gray-800/60"
                          : "border-gray-200 bg-white/40",
                        "font-inter"
                      )}
                      style={{ width: month.days * colWidth, minWidth: 40 }}
                    >
                      {`${format(month.start, "MMMM")} (${
                        getMonth(month.start) + 1
                      }) ${getYear(month.start)}`}
                    </div>
                  ))}
                </div>
              </div>
              {/* Linha de semanas */}
              <div className="flex font-inter">
                <div className="gantt-col w-48" />
                <div className="gantt-col w-32" />
                <div className="gantt-col w-24" />
                <div className="gantt-col w-20" />
                <div className="gantt-col w-24" />
                <div className="gantt-col w-24" />
                <div className="gantt-col w-16" />
                <div className="gantt-col w-20" />
                <div className="flex-1 relative flex">
                  {weekRanges.map((week, i) => (
                    <div
                      key={i}
                      className={clsx(
                        "text-xs text-center border-l",
                        theme === "dark"
                          ? "border-gray-700 bg-gray-800/40"
                          : "border-gray-100 bg-white/30",
                        "font-grotesk"
                      )}
                      style={{ width: week.days * colWidth, minWidth: 20 }}
                    >
                      {`Sem ${getWeek(week.start)}`}
                    </div>
                  ))}
                </div>
              </div>
              {/* Linha de dias */}
              <div className="flex font-inter">
                <div className="gantt-col w-48 font-semibold font-inter">
                  Nome
                </div>
                <div className="gantt-col w-32 font-semibold font-inter">
                  Responsável
                </div>
                <div className="gantt-col w-24 font-semibold font-inter">
                  Status
                </div>
                <div className="gantt-col w-20 font-semibold font-inter">
                  Prioridade
                </div>
                <div className="gantt-col w-24 font-semibold font-inter">
                  Início
                </div>
                <div className="gantt-col w-24 font-semibold font-inter">Fim</div>
                <div className="gantt-col w-16 font-semibold text-center font-inter">
                  Duração
                </div>
                <div className="gantt-col w-20 font-semibold text-center font-inter">
                  Progresso
                </div>
                <div className="flex-1 relative">
                  <div className="flex">
                    {Array.from({ length: totalDays }).map((_, i) => {
                      const day = addDays(ganttStart, i);
                      const prevDay = i > 0 ? addDays(ganttStart, i - 1) : null;
                      const isNewMonth = prevDay
                        ? !isSameMonth(day, prevDay)
                        : true;
                      const isNewWeek = prevDay
                        ? !isSameWeek(day, prevDay, { weekStartsOn: 1 })
                        : true;
                      return (
                        <div
                          key={i}
                          className={clsx(
                            "text-xs text-center border-l font-grotesk",
                            theme === "dark"
                              ? "border-gray-700"
                              : "border-gray-100",
                            isNewMonth
                              ? theme === "dark"
                                ? "bg-cyan-900/30"
                                : "bg-blue-100/40"
                              : isNewWeek
                              ? theme === "dark"
                                ? "bg-cyan-800/20"
                                : "bg-blue-50/40"
                              : theme === "dark"
                              ? "bg-gray-900/10"
                              : "bg-white/10",
                            isNewMonth && "border-l-4 border-blue-500" // Destaque para o início do mês
                          )}
                          title={`${format(day, "dd/MM/yyyy")}`}
                          style={{ minWidth: colWidth, width: colWidth }}
                        >
                          {format(day, "d")}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            {/* Indicador de Hoje */}
            {(() => {
              const today = startOfDay(new Date());
              if (
                today >= startOfDay(ganttStart) &&
                today <= startOfDay(ganttEnd)
              ) {
                const daysFromStart = differenceInDays(
                  today,
                  startOfDay(ganttStart)
                );
                const left = daysFromStart * colWidth;
                return (
                  <div
                    className={clsx(
                      "absolute top-0 bottom-0 z-20 rounded pointer-events-none"
                    )}
                    style={{
                      left,
                      width: 3,
                      background:
                        "linear-gradient(to bottom, #38bdf8 60%, #2563eb 100%)",
                      opacity: 0.85,
                    }}
                    title="Hoje"
                    role="presentation"
                    aria-hidden="true"
                  />
                );
              }
              return null;
            })()}
            {/* Linhas dos eventos hierárquicos */}
            <div className="relative z-10 font-grotesk">
              {renderGanttRows(
                tasksTree,
                0,
                ganttStart,
                totalDays,
                colWidth,
                barRefs,
                editing,
                setEditing,
                handleSaveEdit,
                setLocalTasks,
                collapsedIds,
                toggleCollapse
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Toasts, modais, etc */}
      {toast && (
        <div
          className={clsx(
            "fixed top-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded shadow-lg z-[9999] font-inter",
            toast.type === "warning"
              ? "bg-yellow-400 text-black"
              : "bg-blue-600 text-white"
          )}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

export default CalendarGantt;
